import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// These environment variables are injected by Vite at build time.
// They will not be undefined in the final build if your .env file is set up correctly.
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Connect to Firebase emulators in development mode
// Emulators allow local testing without affecting production data
const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
if (import.meta.env.DEV && useEmulator) {
    try {
        connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
        connectFirestoreEmulator(db, 'localhost', 8081);
        connectStorageEmulator(storage, 'localhost', 9199);
        console.log('🔧 Firebase emulators connected (Auth: 9099, Firestore: 8081, Storage: 9199)');

        // Filter out noisy emulator bridge errors in console
        // These are SDK internal errors that don't affect functionality
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
            const message = args.join(' ');

            // List of known emulator bridge errors to suppress (not user-facing)
            const suppressedPatterns = [
                'securetoken.googleapis.com/v1/token', // Token refresh attempts (SDK internal)
                'identitytoolkit.googleapis.com', // Auth API calls (normal operation)
            ];

            // Only suppress if it's a known bridge error AND status is 400
            const isBridgeError = suppressedPatterns.some(pattern => message.includes(pattern)) &&
                message.includes('400');

            // Always show legitimate errors (auth/user-token-expired, permission-denied, etc.)
            const isLegitimateError = message.includes('auth/') ||
                message.includes('PERMISSION_DENIED') ||
                message.includes('FirebaseError');

            if (!isBridgeError || isLegitimateError) {
                originalConsoleError.apply(console, args);
            }
        };
    } catch (error) {
        console.warn('⚠️ Firebase emulator connection failed. Using production Firebase.', error);
    }
} else {
    console.log('🚀 Using production Firebase');
}

export { auth, db, storage, app };